import React, { useEffect, useState } from "react";
import { Table, Popconfirm, Button, Tooltip, Tag } from "antd";
import {
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import JsonModal from "./JsonModal";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../../redux/dashboardLogger/actions";
import moment from "moment";

const LogsTable = ({ tableParams, setTableParams }) => {
  // To track which row's JSON is being viewed
  const [activeRow, setActiveRow] = useState({ key: null, field: null });
  // Modal state for JSON viewer
  const [jsonModal, setJsonModal] = useState({
    visible: false,
    title: "",
    jsonData: null,
  });

  //   read store
  const { logs } = useSelector((state) => {
    return {
      logs: state?.DashboardLoggerReducer?.logs,
    };
  });

  // Dynamic columns based on log object keys, excluding _id
  const logsArray = Array.isArray(logs?.response?.logs)
    ? logs.response.logs
    : [];
  const allKeys = logsArray.length > 0 ? Object.keys(logsArray[0]) : [];
  const excludeKeys = ["_id", "__v", "timestamp", "createdAt"];
  // Serial number column
  const serialColumn = {
    title: "#",
    key: "serial",
    align: "center",
    width: 60,
    render: (_, __, idx) => {
      return (tableParams.current - 1) * tableParams.pageSize + idx + 1;
    },
  };

  const columns = [
    serialColumn,
    ...allKeys
      .filter((key) => !excludeKeys.includes(key))
      .map((key) => {
        // Format date columns
        if (["updatedAt"].includes(key)) {
          return {
            title: key.charAt(0).toUpperCase() + key.slice(1),
            dataIndex: key,
            key,
            ellipsis: true,
            render: (val) =>
              val ? moment(val).format("YYYY-MM-DD HH:mm:ss") : "-",
          };
        }
        // Color for method
        if (key === "method") {
          return {
            title: "Method",
            dataIndex: key,
            key,
            ellipsis: true,
            render: (val) => {
              let color = "default";
              if (val === "GET") color = "green";
              else if (val === "POST") color = "blue";
              else if (val === "PUT") color = "orange";
              else if (val === "DELETE") color = "red";
              return (
                <Tag color={color} style={{ fontWeight: 500 }}>
                  {val}
                </Tag>
              );
            },
          };
        }
        // Color for level
        if (key === "level") {
          return {
            title: "Level",
            dataIndex: key,
            key,
            ellipsis: true,
            render: (val) => {
              let color = "default";
              if (val?.toLowerCase() === "info") color = "blue";
              else if (val?.toLowerCase() === "warn") color = "orange";
              else if (val?.toLowerCase() === "error") color = "red";
              else if (val?.toLowerCase() === "debug") color = "purple";
              return (
                <Tag color={color} style={{ fontWeight: 500 }}>
                  {val}
                </Tag>
              );
            },
          };
        }
        // Add eye icon for request/response/user columns
        if (["request", "response", "user"].includes(key)) {
          return {
            title: key.charAt(0).toUpperCase() + key.slice(1),
            dataIndex: key,
            key,
            ellipsis: true,
            render: (val, record) => {
              const isActive =
                jsonModal.visible &&
                activeRow.key === record.key &&
                activeRow.field === key;
              return (
                <Tooltip
                  title={isActive ? `Hide ${key} JSON` : `View ${key} JSON`}
                >
                  <Button
                    icon={isActive ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    size="small"
                    type={isActive ? "default" : "primary"}
                    onClick={() => {
                      if (isActive) {
                        setJsonModal({
                          visible: false,
                          title: "",
                          jsonData: null,
                        });
                        setActiveRow({ key: null, field: null });
                      } else {
                        setJsonModal({
                          visible: true,
                          title: `${
                            key.charAt(0).toUpperCase() + key.slice(1)
                          } JSON`,
                          jsonData: val,
                        });
                        setActiveRow({ key: record.key, field: key });
                      }
                    }}
                  />
                </Tooltip>
              );
            },
          };
        }
        return {
          title: key.charAt(0).toUpperCase() + key.slice(1),
          dataIndex: key,
          key,
          ellipsis: true,
        };
      }),
  ];

  // Use _id as key for each row
  const data = logsArray.map((log) => ({ ...log, key: log._id }));

  const handleTableChange = (pag) => {
    //console.log(pag, "pag");
    setTableParams(pag);
  };

  return (
    <>
      <Table
        columns={columns}
        dataSource={data}
        loading={logs?.loading}
        pagination={{
          total: logs?.response?.total,
          current: tableParams.current,
          pageSize: tableParams.pageSize,
          showSizeChanger: logs?.response?.totalPages > 10 ? true : false,
          showQuickJumper: logs?.response?.totalPages > 0 ? true : false,
          pageSizeOptions: ["5", "10", "20", "50"],
        }}
        onChange={handleTableChange}
        scroll={{ x: true }}
      />
      <JsonModal
        visible={jsonModal.visible}
        onClose={() => {
          setJsonModal({ visible: false, title: "", jsonData: null });
          setActiveRow({ key: null, field: null });
        }}
        title={jsonModal.title}
        jsonData={jsonModal.jsonData}
      />
    </>
  );
};

export default LogsTable;
